import { TRACK } from '../game/tuning';
import type { ObstacleSystem } from './ObstacleSystem';
import type { CellSystem } from './CellSystem';

type Rng = () => number;
type Lane = -1 | 0 | 1;

const LANES: Lane[] = [-1, 0, 1];

/**
 * Authored pattern spawner with a distance-based difficulty ramp. New hazard
 * families unlock progressively; every few dense patterns a recovery beat
 * with cells gives the player room to breathe. All randomness flows through
 * the seeded RNG for deterministic runs.
 */
export class PatternDirector {
  private gapRemaining = 18;
  private hazardStreak = 0;
  private patternsSpawned = 0;

  constructor(
    private readonly obstacles: ObstacleSystem,
    private readonly cells: CellSystem,
    private rng: Rng,
  ) {}

  setRng(rng: Rng): void {
    this.rng = rng;
  }

  reset(): void {
    this.gapRemaining = 18;
    this.hazardStreak = 0;
    this.patternsSpawned = 0;
  }

  update(distanceDelta: number, totalDistance: number): void {
    this.gapRemaining -= distanceDelta;
    if (this.gapRemaining > 0) return;
    const length = this.spawnPattern(totalDistance);
    // Gap shrinks from ~30 to ~15 as distance grows; patterns also get longer.
    const difficulty = Math.min(totalDistance / 2600, 1);
    const baseGap = 30 - 15 * difficulty;
    this.gapRemaining = length + baseGap + this.rng() * 8;
  }

  private lane(): Lane {
    return LANES[Math.floor(this.rng() * 3)];
  }

  /** Spawns one pattern at the far edge; returns its approximate length. */
  private spawnPattern(distance: number): number {
    this.patternsSpawned += 1;

    // Recovery beat after a streak of hazards.
    if (this.hazardStreak >= 4) {
      this.hazardStreak = 0;
      return this.cellBreather();
    }

    const roll = this.rng();
    this.hazardStreak += 1;

    if (distance < 60) {
      this.hazardStreak = 0;
      return this.introCells();
    }
    if (distance < 350) {
      return roll < 0.65 ? this.firewallRow() : this.cellArc();
    }
    if (distance < 700) {
      if (roll < 0.4) return this.firewallRow();
      if (roll < 0.7) return this.pylonPair();
      return this.cellArc();
    }
    if (distance < 1100) {
      if (roll < 0.3) return this.firewallRow();
      if (roll < 0.55) return this.pylonPair();
      if (roll < 0.8) return this.droneSweep();
      return this.cellArc();
    }
    if (distance < 1600) {
      if (roll < 0.25) return this.gateWall();
      if (roll < 0.45) return this.droneSweep();
      if (roll < 0.65) return this.pylonPair();
      if (roll < 0.85) return this.firewallRow();
      return this.cellArc();
    }
    // Full mix + combos.
    if (roll < 0.22) return this.comboCorridor();
    if (roll < 0.42) return this.gateWall();
    if (roll < 0.6) return this.droneSweep();
    if (roll < 0.75) return this.pylonPair();
    if (roll < 0.9) return this.firewallRow();
    return this.cellArc();
  }

  // --- Patterns. All spawn around TRACK.spawnZ with negative offsets ahead.

  private introCells(): number {
    const z = TRACK.spawnZ;
    const lane = this.lane();
    for (let i = 0; i < 5; i += 1) this.cells.spawn(lane, z - i * 3);
    return 15;
  }

  private cellBreather(): number {
    const z = TRACK.spawnZ;
    const laneA = this.lane();
    const laneB = this.lane();
    for (let i = 0; i < 4; i += 1) this.cells.spawn(laneA, z - i * 3);
    for (let i = 0; i < 4; i += 1) this.cells.spawn(laneB, z - 14 - i * 3);
    this.cells.spawn(laneB, z - 27, true);
    return 30;
  }

  private cellArc(): number {
    const z = TRACK.spawnZ;
    // A sweep of cells crossing lanes: teaches proactive lane planning.
    const order: Lane[] = this.rng() < 0.5 ? [-1, 0, 1] : [1, 0, -1];
    let offset = 0;
    for (const lane of order) {
      for (let i = 0; i < 2; i += 1) {
        this.cells.spawn(lane, z - offset);
        offset += 3;
      }
    }
    if (this.rng() < 0.35) this.cells.spawn(order[2], z - offset, true);
    return offset + 3;
  }

  private firewallRow(): number {
    const z = TRACK.spawnZ;
    const open = this.lane();
    for (const lane of LANES) {
      if (lane === open) continue;
      if (this.rng() < 0.75) this.obstacles.spawn('firewall', lane, z);
    }
    // Reward the open lane.
    if (this.rng() < 0.6) {
      for (let i = 0; i < 3; i += 1) this.cells.spawn(open, z - 4 - i * 3);
    }
    return 14;
  }

  private pylonPair(): number {
    const z = TRACK.spawnZ;
    const openA = this.lane();
    for (const lane of LANES) {
      if (lane !== openA) this.obstacles.spawn('pylon', lane, z);
    }
    const openB = this.lane();
    for (const lane of LANES) {
      if (lane !== openB) this.obstacles.spawn('pylon', lane, z - 16);
    }
    if (this.rng() < 0.5) this.cells.spawn(openB, z - 8);
    return 30;
  }

  private droneSweep(): number {
    const z = TRACK.spawnZ;
    const from = this.lane();
    let to = this.lane();
    if (to === from) to = from === 1 ? -1 : ((from + 1) as Lane);
    this.obstacles.spawn('drone', from, z, {
      toLane: to,
      phase: this.rng() * Math.PI * 2,
      speed: 1.6 + this.rng() * 0.9,
    });
    if (this.rng() < 0.5) {
      this.obstacles.spawn('firewall', this.lane(), z - 18);
    }
    return 26;
  }

  private gateWall(): number {
    const z = TRACK.spawnZ;
    const open = this.lane();
    for (const lane of LANES) {
      if (lane !== open) this.obstacles.spawn('gate', lane, z);
    }
    for (let i = 0; i < 3; i += 1) this.cells.spawn(open, z - 3 - i * 3);
    return 16;
  }

  private comboCorridor(): number {
    const z = TRACK.spawnZ;
    // Gate, then a drone patrolling the free space, then a firewall row.
    const open = this.lane();
    for (const lane of LANES) {
      if (lane !== open) this.obstacles.spawn('gate', lane, z);
    }
    let droneTo = this.lane();
    if (droneTo === open) droneTo = open === 1 ? -1 : ((open + 1) as Lane);
    this.obstacles.spawn('drone', open, z - 14, {
      toLane: droneTo,
      phase: this.rng() * Math.PI * 2,
      speed: 2,
    });
    const open2 = this.lane();
    for (const lane of LANES) {
      if (lane !== open2 && this.rng() < 0.8) this.obstacles.spawn('firewall', lane, z - 26);
    }
    this.cells.spawn(open2, z - 30);
    return 40;
  }
}
